import { useCallback, useEffect, useRef } from 'react'
import _ from 'lodash'
import { KeyboardEvent } from 'react'  // Use React's KeyboardEvent

interface UseDrawerNavigationProps {
    containerRef: React.RefObject<HTMLElement>
}

export const useDrawerNavigation = ({ containerRef }: UseDrawerNavigationProps) => {
    // Ref to store the list of navigable elements
    const navElements = useRef<Element[]>([])

    // Debounced function to update navigation elements
    const updateNavList = useCallback(
        _.debounce(() => {
            if (!containerRef.current) return

            // Query only headers and action items
            const elements = containerRef.current.querySelectorAll('[data-drawer-nav="header"], [data-drawer-nav="item"]')
            navElements.current = Array.from(elements)
        }, 100),
        [containerRef]
    )

    const handleKeyDown = useCallback((e: KeyboardEvent<HTMLElement>) => {
        const target = e.target as HTMLElement
        if (!target.hasAttribute('data-drawer-nav')) return

        const currentNavType = target.getAttribute('data-drawer-nav')

        switch (e.key) {
            case 'ArrowUp':
            case 'ArrowDown': {
                e.preventDefault()

                // If it's already a header/item, find next visible item in sequence
                if (currentNavType === 'header' || currentNavType === 'item') {
                    const currentIndex = navElements.current.indexOf(target)
                    let nextIndex = e.key === 'ArrowUp'
                        ? currentIndex - 1
                        : currentIndex + 1

                    // Find next visible element
                    while (nextIndex >= 0 && nextIndex < navElements.current.length) {
                        const nextElement = navElements.current[nextIndex] as HTMLElement

                        // Headers are always visible
                        if (nextElement.getAttribute('data-drawer-nav') === 'header') {
                            nextElement.focus()
                            break
                        }

                        // For items, check if they're in a collapsible section
                        const container = nextElement.closest('[data-drawer-container]')
                        if (container) {
                        // Item is in a collapsible section, check if expanded
                            const headerElement = container.querySelector('[data-drawer-nav="header"]')
                            if (headerElement?.getAttribute('aria-expanded') === 'true') {
                                nextElement.focus()
                                break
                            }
                        } else {
                        // Item is standalone, always visible
                            nextElement.focus()
                            break
                        }

                        // Skip this item and try next one
                        nextIndex = e.key === 'ArrowUp' ? nextIndex - 1 : nextIndex + 1
                    }
                }
                // If we're inside a header's content
                else {
                    const container = target.closest('[data-drawer-container]')
                    if (container) {
                        if (e.key === 'ArrowUp') {
                        // Focus the current section's header
                            const headerElement = container.querySelector('[data-drawer-nav="header"]') as HTMLElement
                            headerElement?.focus()
                        } else {
                        // Find current header's index and focus the next visible header/item
                            const headerElement = container.querySelector('[data-drawer-nav="header"]')
                            const currentHeaderIndex = navElements.current.indexOf(headerElement)
                            let nextIndex = currentHeaderIndex + 1

                            while (nextIndex < navElements.current.length) {
                                const nextElement = navElements.current[nextIndex] as HTMLElement

                                // Headers are always visible
                                if (nextElement.getAttribute('data-drawer-nav') === 'header') {
                                    nextElement.focus()
                                    break
                                }

                                // For items, check visibility as before
                                const itemContainer = nextElement.closest('[data-drawer-container]')
                                if (itemContainer) {
                                    const itemHeader = itemContainer.querySelector('[data-drawer-nav="header"]')
                                    if (itemHeader?.getAttribute('aria-expanded') === 'true') {
                                        nextElement.focus()
                                        break
                                    }
                                } else {
                                    // Standalone item
                                    nextElement.focus()
                                    break
                                }

                                nextIndex++
                            }
                        }
                    }
                }
                break
            }
            case 'ArrowLeft': {
                let headerElement: HTMLElement | null = null

                if (currentNavType === 'header') {
                    headerElement = target as HTMLElement
                } else {
                    // If not a header/item, find parent container, then get its header
                    const container = target.closest('[data-drawer-container]')
                    if (container) {
                        headerElement = container.querySelector('[data-drawer-nav="header"]') as HTMLElement
                    }
                }

                if (headerElement && headerElement.getAttribute('aria-expanded') === 'true') {
                    e.preventDefault()
                    headerElement.click() // Collapse section
                    headerElement.focus()
                }
                break
            }

            case 'ArrowRight': {
                // If it's a header and it's collapsed, expand it
                if (currentNavType === 'header' && target.getAttribute('aria-expanded') === 'false') {
                    e.preventDefault()
                    target.click()
                }
                break
            }
        }
    }, [])

    return {
        updateNavList,
        handleKeyDown
    }
}
